import type { ContributionEntry } from "@/hooks/useBatchEntry";
import { isContributionEntryMethod } from "./contribution-entry-methods";

/**
 * A half-entered contribution batch, kept so an accidental close, a reload, a crash or a discarded tab does
 * not throw away a whole collection's worth of typing (#6069).
 *
 * It lives in sessionStorage on purpose. That survives every loss the issue names (a reload, a browser crash
 * restore and a discarded tab all keep a tab's session storage) but not the tab being closed, so donor names
 * and amounts do not linger on a shared church computer the way localStorage would leave them. The saved-batch
 * drafts already use a session store for the same reason.
 *
 * Scoped to one organization AND one user. Two people can share a tab (one signs out, another signs in), and a
 * batch holds donor names and amounts: the key names both, the record repeats them, and a draft whose stored
 * scope does not match the requester is refused. Signing out also clears every draft (see useAuth).
 */
export interface ClassicBatchEntryDraftScope {
  organizationId: string;
  actorId: string;
}

export interface ClassicBatchEntryDraft {
  version: 1;
  scope: ClassicBatchEntryDraftScope;
  savedAt: string;
  /** Set when a save of these gifts was started. A draft that still carries it was not confirmed saved. */
  submittedAt?: string;
  batchDate: string;
  eventName: string;
  autoPostToGL: boolean;
  entries: ContributionEntry[];
}

/** "unavailable" means no recovery copy of the current batch exists (storage blocked, full, or the batch too large). */
export type ClassicBatchEntryDraftWrite = "stored" | "unavailable";

type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type SweepableStorage = Pick<Storage, "length" | "key" | "removeItem">;

const STORAGE_PREFIX = "forge:classic-batch-entry-draft:v1:";
/**
 * Far above a real collection (hundreds of gifts at most). Past it, or past what storage will hold, the write
 * reports "unavailable" and the stale copy is removed, so a reload never restores an outdated part of a batch.
 */
const MAX_DRAFT_ENTRIES = 5000;

const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

export function classicBatchEntryDraftStorageKey(scope: ClassicBatchEntryDraftScope): string {
  return `${STORAGE_PREFIX}${encodeURIComponent(scope.organizationId)}:${encodeURIComponent(scope.actorId)}`;
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const isAllocation = (value: unknown): boolean =>
  isObject(value)
  && typeof value.fundId === "string" && value.fundId.trim().length > 0
  && typeof value.fundName === "string"
  && typeof value.amount === "number" && Number.isFinite(value.amount)
  && (value.pledgeId === undefined || typeof value.pledgeId === "string")
  && (value.restrictionType === undefined || value.restrictionType === null || typeof value.restrictionType === "string")
  && (value.restriction_metadata_availability === undefined
    || value.restriction_metadata_availability === "confirmed"
    || value.restriction_metadata_availability === "unavailable");

const isEntry = (value: unknown): value is ContributionEntry =>
  isObject(value)
  && typeof value.id === "string" && value.id.trim().length > 0
  && (value.personId === null || typeof value.personId === "string")
  && typeof value.personName === "string"
  && (value.isAnonymous === undefined || typeof value.isAnonymous === "boolean")
  && (value.guestName === undefined || typeof value.guestName === "string")
  && (value.checkNumber === undefined || typeof value.checkNumber === "string")
  && isContributionEntryMethod(value.method)
  && typeof value.amount === "number" && Number.isFinite(value.amount) && value.amount > 0
  && Array.isArray(value.allocations) && value.allocations.length > 0 && value.allocations.every(isAllocation);

const isScope = (value: unknown): value is ClassicBatchEntryDraftScope =>
  isObject(value) && isNonEmptyString(value.organizationId) && isNonEmptyString(value.actorId);

/** True for a well-formed draft; with `scope`, only one that was kept for that organization and user. */
export function isClassicBatchEntryDraft(value: unknown, scope?: ClassicBatchEntryDraftScope): value is ClassicBatchEntryDraft {
  return isObject(value)
    && value.version === 1
    && isScope(value.scope)
    && (scope === undefined || (value.scope.organizationId === scope.organizationId && value.scope.actorId === scope.actorId))
    && typeof value.savedAt === "string" && !Number.isNaN(Date.parse(value.savedAt))
    && (value.submittedAt === undefined || (typeof value.submittedAt === "string" && !Number.isNaN(Date.parse(value.submittedAt))))
    && typeof value.batchDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.batchDate)
    && typeof value.eventName === "string"
    && typeof value.autoPostToGL === "boolean"
    && Array.isArray(value.entries)
    && value.entries.length > 0 && value.entries.length <= MAX_DRAFT_ENTRIES
    && value.entries.every(isEntry);
}

function resolveStorage<T>(storage: T | undefined, pick: () => T | undefined): T | undefined {
  try {
    return storage ?? (typeof window === "undefined" ? undefined : pick());
  } catch {
    // Reading the property itself throws when storage is blocked.
    return undefined;
  }
}

const sessionStore = () => resolveStorage<DraftStorage>(undefined, () => window.sessionStorage);

/** A stored draft kept for this organization and user, or null when there is none or it is not theirs. */
export function readClassicBatchEntryDraft(
  scope: ClassicBatchEntryDraftScope | null | undefined,
  storage?: DraftStorage,
): ClassicBatchEntryDraft | null {
  try {
    const resolved = storage ?? sessionStore();
    if (!scope || !isScope(scope) || !resolved) return null;
    const raw = resolved.getItem(classicBatchEntryDraftStorageKey(scope));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isClassicBatchEntryDraft(parsed, scope) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeClassicBatchEntryDraft(
  scope: ClassicBatchEntryDraftScope | null | undefined,
  draft: Omit<ClassicBatchEntryDraft, "version" | "savedAt" | "scope" | "submittedAt">,
  storage?: DraftStorage,
  now: Date = new Date(),
  // A rewrite (the person edits the batch again) keeps the "a save was started" marker: nothing has confirmed that
  // save, so a later restore offer must still warn. Only a confirmed save clears it, by writing the leftovers without it.
  options: { carrySubmittedMarker?: boolean } = {},
): ClassicBatchEntryDraftWrite {
  const resolved = (() => { try { return storage ?? sessionStore(); } catch { return undefined; } })();
  if (!scope || !isScope(scope) || !resolved) return "unavailable";
  const key = classicBatchEntryDraftStorageKey(scope);
  try {
    const submittedAt = options.carrySubmittedMarker === false ? undefined : readClassicBatchEntryDraft(scope, resolved)?.submittedAt;
    const record: ClassicBatchEntryDraft = { ...draft, ...(submittedAt === undefined ? {} : { submittedAt }), version: 1, scope: { organizationId: scope.organizationId, actorId: scope.actorId }, savedAt: now.toISOString() };
    // Never persist something the reader would refuse: a draft that cannot be restored is worse than none.
    if (!isClassicBatchEntryDraft(record, scope)) throw new Error("draft is not storable");
    resolved.setItem(key, JSON.stringify(record));
    return "stored";
  } catch {
    // The earlier snapshot is now out of date: restoring it after a reload would silently lose the newer gifts.
    try { resolved.removeItem(key); } catch { /* nothing more to do */ }
    return "unavailable";
  }
}

/**
 * Records that a save of the stored gifts has started. If the page is lost before the save is confirmed, the
 * restore offer can then warn that those gifts may already be saved.
 */
export function markClassicBatchEntryDraftSubmitted(
  scope: ClassicBatchEntryDraftScope | null | undefined,
  storage?: DraftStorage,
  now: Date = new Date(),
): void {
  try {
    const resolved = storage ?? sessionStore();
    const draft = readClassicBatchEntryDraft(scope, resolved);
    if (!scope || !resolved || !draft) return;
    resolved.setItem(classicBatchEntryDraftStorageKey(scope), JSON.stringify({ ...draft, submittedAt: now.toISOString() }));
  } catch {
    // The batch itself is unaffected.
  }
}

/**
 * Removes from the stored draft the gifts a confirmed save wrote, and nothing else. A save that finishes late
 * (the dialog was left and reopened meanwhile) must not delete newer gifts someone has since entered: only the
 * submitted ids go, and the draft is cleared only when none remain.
 */
export function forgetSavedClassicBatchEntries(
  scope: ClassicBatchEntryDraftScope | null | undefined,
  savedEntryIds: readonly string[],
  storage?: DraftStorage,
  now: Date = new Date(),
): void {
  try {
    const resolved = storage ?? sessionStore();
    const draft = readClassicBatchEntryDraft(scope, resolved);
    if (!scope || !resolved || !draft) return;
    const saved = new Set(savedEntryIds);
    const remaining = draft.entries.filter((row) => !saved.has(row.id));
    if (remaining.length === 0) {
      resolved.removeItem(classicBatchEntryDraftStorageKey(scope));
      return;
    }
    if (remaining.length === draft.entries.length) return;
    writeClassicBatchEntryDraft(scope, { batchDate: draft.batchDate, eventName: draft.eventName, autoPostToGL: draft.autoPostToGL, entries: remaining }, resolved, now, { carrySubmittedMarker: false });
  } catch {
    // Nothing to forget when storage is unavailable.
  }
}

export function clearClassicBatchEntryDraft(
  scope: ClassicBatchEntryDraftScope | null | undefined,
  storage?: DraftStorage,
): void {
  try {
    const resolved = storage ?? sessionStore();
    if (!scope || !isScope(scope) || !resolved) return;
    resolved.removeItem(classicBatchEntryDraftStorageKey(scope));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Every draft in this tab, whoever kept it. Called on sign-out so the next person on the device sees none. */
export function clearAllClassicBatchEntryDrafts(storage?: SweepableStorage): void {
  try {
    const resolved = storage ?? resolveStorage<SweepableStorage>(undefined, () => window.sessionStorage);
    if (!resolved) return;
    const keys: string[] = [];
    for (let index = 0; index < resolved.length; index += 1) {
      const key = resolved.key(index);
      if (key !== null && key.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    // Collect first: removing while iterating shifts the indexes.
    keys.forEach((key) => resolved.removeItem(key));
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
